/**
 * Turns an admin-authored icon code into something the storefront can actually paint.
 *
 * The admin lets an editor set a category's icon as a snippet of markup, and every category in the
 * catalogue uses a Font Awesome class for it — `<i class="fa fa-tshirt"></i>` and so on. The admin
 * loads Font Awesome, so those render there. The storefront never has: it ships no icon font and no
 * other part of it uses `fa-` classes. The snippet was passed straight through to `[innerHTML]`, so
 * on the public site every one of those icons rendered as an `<i>` with nothing inside it — the
 * blank grey circles on the home page's Popular Categories, and the same gap on the category, deals,
 * events and blog pages.
 *
 * Rather than pull a whole icon font into the storefront bundle for this, the icon NAME is mapped to
 * an emoji glyph, which needs no font download and is already part of this page's visual language
 * (the "Add your brand" tile uses one). The mapping is by Font Awesome's own icon name, so it holds
 * regardless of which `fa`/`fas`/`far` prefix an editor typed.
 *
 * Nothing here invents category data: an unmapped icon falls back to the category's own initials,
 * and a category with no icon at all does too. The one rule it enforces is that the tile is never
 * blank.
 */

/** Font Awesome icon name -> a glyph that reads as the same thing. */
const ICON_GLYPHS: Record<string, string> = {
  baby: '👶',
  'book-open': '📖',
  book: '📖',
  briefcase: '💼',
  calendar: '📅',
  'calendar-alt': '📅',
  camera: '📷',
  campground: '⛺',
  cannabis: '🌿',
  car: '🚗',
  clock: '⌚',
  'concierge-bell': '🛎️',
  couch: '🛋️',
  desktop: '🖥️',
  diamond: '💎',
  gem: '💎',
  female: '👗',
  film: '🎬',
  'football-ball': '🏈',
  futbol: '⚽',
  gamepad: '🎮',
  gift: '🎁',
  glasses: '👓',
  'graduation-cap': '🎓',
  heart: '💗',
  home: '🏡',
  house: '🏡',
  hotel: '🏨',
  'info-circle': 'ℹ️',
  kiss: '💋',
  laptop: '💻',
  mobile: '📱',
  'mobile-alt': '📱',
  'mug-hot': '☕',
  'network-wired': '🌐',
  globe: '🌐',
  paw: '🐾',
  'pencil-alt': '✏️',
  pencil: '✏️',
  pills: '💊',
  plane: '✈️',
  print: '🖨️',
  server: '🖧',
  'shield-alt': '🛡️',
  shield: '🛡️',
  'shoe-prints': '👟',
  'shopping-basket': '🧺',
  'shopping-cart': '🛒',
  'shopping-bag': '🛍️',
  smile: '😊',
  smoking: '🚬',
  spa: '🧖',
  stethoscope: '🩺',
  store: '🏬',
  suitcase: '🧳',
  taxi: '🚕',
  'theater-masks': '🎭',
  'thumbs-up': '👍',
  tools: '🛠️',
  tshirt: '👕',
  shirt: '👕',
  tv: '📺',
  utensils: '🍽️',
  'wine-bottle': '🍷',
  'wine-glass': '🍷',
  music: '🎵',
  bicycle: '🚲',
  dumbbell: '🏋️',
  leaf: '🌱',
  seedling: '🌱',
  star: '⭐',
  tag: '🏷️',
  tags: '🏷️',
  ticket: '🎟️',
  'ticket-alt': '🎟️',
  truck: '🚚',
  wrench: '🔧',
};

/**
 * The glyph for an icon code, or '' when the code names an icon we have no glyph for.
 *
 * Reads the icon name out of whatever the editor typed — `<i class="fa fa-tshirt"></i>`,
 * `fas fa-tshirt`, or a bare `tshirt` all resolve the same way.
 */
export function categoryIconGlyph(iconCode: string | null | undefined): string {
  const code = String(iconCode || '').trim();
  if (!code) return '';

  // An editor may also paste an emoji directly; if there is no `fa-` class, take the text as-is.
  const named = code.match(/fa-([a-z0-9-]+)/i);
  if (!named) {
    const bare = code.replace(/<[^>]*>/g, '').trim();
    return bare.length <= 4 ? bare : '';
  }

  return ICON_GLYPHS[named[1].toLowerCase()] || '';
}

/**
 * Initials for a name, used when there is no artwork and no glyph.
 *
 * Two letters from a multi-word name ("Health & Beauty" -> HB), two from a single word. Joining
 * words are skipped so the mark comes from the words that carry the meaning.
 */
export function categoryInitials(name: string | null | undefined): string {
  const words = String(name || '')
    .split(/[\s/&,-]+/)
    .map((word) => word.trim())
    .filter((word) => word && !/^(and|the|of|for)$/i.test(word));

  if (!words.length) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * What a tile should show in place of an image: the editor's icon if we can paint it, otherwise the
 * name's initials. Never returns an empty string for a named category, which is the point.
 */
export function categoryFallbackMark(iconCode: string | null | undefined, name: string | null | undefined): string {
  return categoryIconGlyph(iconCode) || categoryInitials(name);
}
